import React, { useRef, useState } from "react";
import { useSelector } from "react-redux";

import { Building2, CheckCircle2, FileText, User } from "lucide-react";
import { toast } from "utils/toast";

import { useCreateInvestigation } from "hooks/useInvestigations";

import { CreateReportModalProps } from "js/components/Dashboard/types";
import { Investigation } from "js/components/ExpressReport/types";
import { Button } from "js/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "js/components/ui/dialog";
import { EmailInput, EmailInputRef } from "js/components/ui/email-input";
import { Label } from "js/components/ui/label";

const CreateReportModal: React.FC<CreateReportModalProps> = ({
  showModal,
  setShowModal,
  credits = {
    pf: 0,
    pm: 0,
    identity_verification: 0,
    blacklist: 0,
  },
}) => {
  const [selectedHistCredit, setSelectedHistCredit] = useState<string | null>(
    null,
  );
  const [disableBtn, setDisableBtn] = useState<boolean>(false);
  const [linkReport, setLinkReport] = useState<string | null>(null);
  const [email, setEmail] = useState<string>("");
  const [isEmailValid, setIsEmailValid] = useState<boolean>(true);

  const emailInputRef = useRef<EmailInputRef>(null);
  const createInvestigationMutation = useCreateInvestigation();

  const { organizationData } = useSelector(
    (state: any) => state.entities.organization,
  );

  const { userData } = useSelector((state: any) => state.entities.user);

  const handleClick = async () => {
    if (selectedHistCredit == null) {
      return toast.error("Selecciona un tipo de reporte");
    }

    // Validate email if provided
    if (email) {
      const isValid = await emailInputRef.current?.validate();
      if (!isValid) {
        return toast.error("Ingresa un correo válido");
      }
    }

    createInvestigationMutation.mutate(
      {
        legal_type: selectedHistCredit,
        prospectus_email: email,
        report_types: ["credit_report"],
      },
      {
        onSuccess: (response: Investigation) => {
          setDisableBtn(true);
          setLinkReport(`reports/${response.id}`);
          toast.success(
            "Se le envió un correo con el link para iniciar el multireporte.",
          );
        },
        // Sin `onError` a proposito: react-query v5 ejecuta el onError de
        // `useMutation` Y el de `mutate`, asi que este duplicaba el toast de
        // error (dos toasts por click). El del hook `useCreateInvestigation`
        // ya muestra el mensaje, y ahora pasa por `getApiErrorMessage`.
      },
    );
  };

  const handleClose = () => {
    setShowModal(false);
    setSelectedHistCredit(null);
    setDisableBtn(false);
    setLinkReport(null);
    setEmail("");
    setIsEmailValid(true);
    emailInputRef.current?.clear();
  };

  return (
    <>
      <Dialog open={showModal} onOpenChange={handleClose}>
        <DialogContent className="sm:max-w-[640px]">
          <DialogHeader className="space-y-1.5">
            <DialogTitle className="text-lg font-semibold text-gray-900">
              Crear Nuevo Reporte
            </DialogTitle>
            <p className="text-sm text-gray-500">
              Selecciona el tipo de reporte crediticio que deseas generar
            </p>
          </DialogHeader>

          {linkReport ? (
            <div className="space-y-6 py-4">
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 space-y-3">
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                    <CheckCircle2 className="w-5 h-5 text-green-600" />
                  </div>
                  <div className="flex-1 space-y-3">
                    <div className="space-y-2">
                      <p className="text-sm font-medium text-green-900">
                        ¡Reporte creado exitosamente!
                      </p>
                      <p className="text-sm text-green-700">
                        Se envió un correo a{" "}
                        <span className="font-semibold text-green-900">
                          {email}
                        </span>{" "}
                        con el enlace para iniciar el reporte.
                      </p>
                      <p className="text-sm text-green-700">
                        Puedes abrir el reporte o cerrar este diálogo.
                      </p>
                    </div>
                    <a
                      href={linkReport ?? "#"}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 text-sm font-medium text-primary hover:underline"
                    >
                      Abrir reporte
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                        />
                      </svg>
                    </a>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-6 py-4">
              {/* Report Type Selection */}
              {organizationData &&
                organizationData.modules.includes("credit_history") && (
                  <div className="space-y-3">
                    <div className="flex items-center gap-2">
                      <FileText className="w-4 h-4 text-gray-600" />
                      <Label className="text-sm font-medium text-gray-900">
                        Tipo de Persona
                      </Label>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      {/* Persona Física Card */}
                      {credits && credits.pf > 0 && (
                        <div
                          onClick={() => {
                            setSelectedHistCredit((prevCard) =>
                              prevCard === "natural" ? null : "natural",
                            );
                          }}
                          className={`relative border rounded-lg p-5 cursor-pointer transition-all ${
                            selectedHistCredit === "natural"
                              ? "border-primary bg-primary/5"
                              : "border-gray-200 hover:border-gray-300 bg-white"
                          }`}
                        >
                          <div className="flex flex-col items-center space-y-3">
                            <div
                              className={`w-12 h-12 rounded-full flex items-center justify-center transition-colors ${
                                selectedHistCredit === "natural"
                                  ? "bg-primary text-white"
                                  : "bg-gray-100 text-gray-600"
                              }`}
                            >
                              <User className="w-6 h-6" />
                            </div>
                            <p className="text-sm font-medium text-gray-900 text-center">
                              Persona Física
                            </p>
                          </div>
                        </div>
                      )}

                      {/* Persona Moral Card */}
                      {credits && credits.pm > 0 && (
                        <div
                          onClick={() => {
                            setSelectedHistCredit((prevCard) =>
                              prevCard === "legal" ? null : "legal",
                            );
                          }}
                          className={`relative border rounded-lg p-5 cursor-pointer transition-all ${
                            selectedHistCredit === "legal"
                              ? "border-primary bg-primary/5"
                              : "border-gray-200 hover:border-gray-300 bg-white"
                          }`}
                        >
                          <div className="flex flex-col items-center space-y-3">
                            <div
                              className={`w-12 h-12 rounded-full flex items-center justify-center transition-colors ${
                                selectedHistCredit === "legal"
                                  ? "bg-primary text-white"
                                  : "bg-gray-100 text-gray-600"
                              }`}
                            >
                              <Building2 className="w-6 h-6" />
                            </div>
                            <p className="text-sm font-medium text-gray-900 text-center">
                              Persona Moral
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

              {/* Email Input Section */}
              <EmailInput
                ref={emailInputRef}
                value={email}
                onChange={setEmail}
                onValidationChange={(isValid) => setIsEmailValid(isValid)}
                label="Correo del Prospecto"
                placeholder="ejemplo@correo.com"
                userData={userData}
                showIcon={false}
                showStatus={false}
              />
            </div>
          )}

          <DialogFooter className="gap-2">
            {linkReport ? (
              <Button type="button" onClick={handleClose}>
                Cerrar
              </Button>
            ) : (
              <>
                <Button type="button" variant="outline" onClick={handleClose}>
                  Cancelar
                </Button>
                <Button
                  onClick={handleClick}
                  disabled={disableBtn || createInvestigationMutation.isPending}
                >
                  {createInvestigationMutation.isPending ? (
                    <span className="flex items-center gap-2">
                      <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                          fill="none"
                        />
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        />
                      </svg>
                      Enviando...
                    </span>
                  ) : (
                    "Enviar"
                  )}
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default CreateReportModal;
